import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { biometricLabel, BiometricIcon } from "../lib/biometrics";
import { passkeysSupported, loginWithPasskey } from "../lib/webauthn";

export default function Login() {
  const { login, register, adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const referralCode = params.get("ref");
  const [inviter, setInviter] = useState(null);
  const [mode, setMode] = useState(referralCode ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [ackDisclaimer, setAckDisclaimer] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (referralCode) {
      api.get(`/auth/referral/${referralCode}`).then((r) => setInviter(r.data)).catch(() => {});
    }
  }, [referralCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !ackDisclaimer) {
      toast.error("Please acknowledge the educational-use disclaimer to continue.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        await register(form.email, form.password, form.name, referralCode, ackDisclaimer);
        localStorage.setItem("pstr_show_tour", "1");
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const biometricLogin = async () => {
    if (!form.email) {
      toast.error("Enter your email first, then use biometrics.");
      return;
    }
    setLoading(true);
    try {
      const r = await loginWithPasskey(form.email);
      adoptSession(r.token, r.user);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Biometric sign-in failed — use your password");
    } finally {
      setLoading(false);
    }
  };

  let submitLabel = "Sign in";
  if (loading) submitLabel = "Please wait…";
  else if (mode !== "login") submitLabel = "Create account";

  return (
    <div className="min-h-screen flex">
      {/* Left: form */}
      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-10 h-10 rounded-xl bg-sage-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-heading text-xl font-medium tracking-tight text-ink-900">Posturra</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">For Massage Therapists</div>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900 mb-3">
            {mode === "login" ? "Welcome back." : "Start your practice."}
          </h1>
          {inviter && mode === "register" && (
            <div className="mb-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta-500/10 text-terracotta-600 text-sm" data-testid="inviter-badge">
              <Sparkles className="w-3.5 h-3.5" />
              Invited by <span className="font-medium">{inviter.inviter_name}</span>{inviter.inviter_clinic ? ` · ${inviter.inviter_clinic}` : ""}
            </div>
          )}
          <p className="text-ink-700 mb-10 leading-relaxed">
            {mode === "login"
              ? "Sign in to view clients, capture posture analyses, and pick up sessions where you left off."
              : "Create a therapist account. Track clients, run AI posture analysis, and finish SOAP notes in seconds."}
          </p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="label-overline block mb-2">Your name</label>
                <input
                  data-testid="auth-name-input"
                  required
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none transition-all"
                  placeholder="Alex Rivera, LMT"
                />
              </div>
            )}
            <div>
              <label className="label-overline block mb-2">Email</label>
              <input
                data-testid="auth-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none transition-all"
                placeholder="you@clinic.com"
              />
            </div>
            <div>
              <label className="label-overline block mb-2">Password</label>
              <input
                data-testid="auth-password-input"
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none transition-all"
                placeholder="At least 6 characters"
              />
              {mode === "login" && (
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-ink-500 hover:text-ink-900 underline underline-offset-4">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading || (mode === "register" && !ackDisclaimer)}
              className="w-full min-h-[52px] rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {submitLabel}
            </button>
            {mode === "register" && (
              <label className="flex items-start gap-3 text-sm text-ink-700 cursor-pointer select-none" data-testid="disclaimer-ack-label">
                <input
                  type="checkbox"
                  data-testid="disclaimer-ack-checkbox"
                  checked={ackDisclaimer}
                  onChange={(e) => setAckDisclaimer(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-paper-200 text-sage-500 focus:ring-sage-500/30"
                />
                <span>
                  I acknowledge that Posturra is for <strong className="text-ink-900">educational and documentation support only</strong>{" "}
                  and does not diagnose medical conditions or replace licensed medical evaluation. I have read the{" "}
                  <Link to="/disclaimer" target="_blank" rel="noopener noreferrer" className="text-terracotta-500 font-medium underline-offset-4 hover:underline" data-testid="disclaimer-link-signup">
                    full disclaimer
                  </Link>
                  .
                </span>
              </label>
            )}
          </form>

          {mode === "login" && passkeysSupported() && (
            <button
              type="button"
              onClick={biometricLogin}
              disabled={loading}
              data-testid="biometric-login-btn"
              className="mt-3 w-full min-h-[48px] rounded-full border border-paper-200 hover:bg-paper-100 disabled:opacity-50 text-ink-700 font-medium transition-all inline-flex items-center justify-center gap-2"
            >
              <BiometricIcon className="w-5 h-5" /> {biometricLabel("Sign in")}
            </button>
          )}

          <div className="mt-8 text-center text-sm text-ink-700">
            {mode === "login" ? (
              <>New here?{" "}
                <button data-testid="auth-toggle-register" type="button" onClick={() => setMode("register")} className="text-terracotta-500 font-medium hover:text-terracotta-600 underline-offset-4 hover:underline">Create an account</button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button data-testid="auth-toggle-login" type="button" onClick={() => setMode("login")} className="text-terracotta-500 font-medium hover:text-terracotta-600 underline-offset-4 hover:underline">Sign in</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden md:block w-1/2 relative overflow-hidden bg-sage-500">
        <img
          src="https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtYXNzYWdlJTIwdGhlcmFwaXN0JTIwY2xpbmljfGVufDB8fHx8MTc3ODg0Nzg1N3ww&ixlib=rb-4.1.0&q=85"
          alt="Massage therapist working"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-ink-900/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.18em] mb-3 opacity-80">Built for hands-on practitioners</div>
          <div className="font-heading text-3xl font-light leading-tight max-w-md">
            From four photos to a focused treatment plan — in about 90 seconds.
          </div>
        </div>
      </div>
    </div>
  );
}
